import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./SuccessStory.css";

import towerImg from "../../../../assets/images/home/succesStory/rightimg.png";
import { resolveMediaUrl } from "../../../../utils/media";

const fallbackSuccessStory = {
  tag: "OUR SUCCESS STORY",
  title: "Palm Towers",
  description:
    "Palm Towers, a landmark just 20 minutes away from the airport. The project comprises 20 residential towers along with commercial spaces, designed with a focus on strong urban planning and architectural quality. IRQGYP gypsum boards were used in ceiling systems and decorative walls, enhancing interior finishes and overall design quality.",
  locationLabel: "Location",
  locationText: "Iraq - Baghdad - distinguished Al-Muthanna area",
  buttonText: "View our success story",
  buttonLink: "/allprojects",
  imageAlt: "Palm Towers",
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

/**
 * Merge CMS shapes: { data }, string/object `value`, plus top-level fields.
 */
const coalesceStoryPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return tryParseJsonObject(raw) || {};
  }
  if (typeof raw !== "object") return null;

  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const titleFromPayload = (c) => {
  const joined = [c?.titleLine1, c?.titleLine2].filter((x) => typeof x === "string" && x.trim()).join(" ").trim();
  return pickString(c?.title, c?.projectTitle, c?.heading, c?.headline, joined);
};

const descriptionFromPayload = (c) =>
  pickString(c?.description, c?.body, c?.text, c?.summary, c?.content, c?.copy);

const getStoryImageCandidates = (c) => {
  if (!c || typeof c !== "object") return [];
  return [
    c.image,
    c.rightImage,
    c.rightImageUrl,
    c.imageUrl,
    c.mediaUrl,
    c.MediaUrl,
    c.photo,
    c.successImage,
    c.storyImage,
    c.heroImage,
    c.introImage,
    c.bannerImage,
  ].filter((x) => x != null && x !== "");
};

const resolveFirstImage = (candidates) => {
  for (const candidate of candidates) {
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const hasMeaningfulStoryPayload = (c) => {
  if (!c || typeof c !== "object") return false;
  if (titleFromPayload(c)) return true;
  if (descriptionFromPayload(c)) return true;
  if (pickString(c.tag, c.sectionTag)) return true;
  if (pickString(c.locationText, c.location, c.address)) return true;
  if (pickString(c.buttonText, c.buttonLink, c.ctaText, c.ctaLink)) return true;
  return Boolean(resolveFirstImage(getStoryImageCandidates(c)));
};

const SuccessStory = ({ content }) => {
  const navigate = useNavigate();

  const { story, imageSrc } = useMemo(() => {
    const payload = coalesceStoryPayload(content);
    const hasReal = hasMeaningfulStoryPayload(payload);

    if (!hasReal || !payload) {
      return {
        story: fallbackSuccessStory,
        imageSrc: towerImg,
      };
    }

    const title = titleFromPayload(payload) || fallbackSuccessStory.title;
    const story = {
      tag: pickString(payload.tag, payload.sectionTag) || fallbackSuccessStory.tag,
      title,
      description: descriptionFromPayload(payload) || fallbackSuccessStory.description,
      locationLabel:
        pickString(payload.locationLabel, payload.locationTitle) || fallbackSuccessStory.locationLabel,
      locationText:
        pickString(payload.locationText, payload.location, payload.address) || fallbackSuccessStory.locationText,
      buttonText: pickString(payload.buttonText, payload.ctaText, payload.buttonLabel) || fallbackSuccessStory.buttonText,
      buttonLink: pickString(payload.buttonLink, payload.ctaLink, payload.link) || fallbackSuccessStory.buttonLink,
      imageAlt: pickString(payload.imageAlt, payload.alt, title) || fallbackSuccessStory.imageAlt,
    };

    const resolved = resolveFirstImage(getStoryImageCandidates(payload));

    return {
      story,
      imageSrc: resolved || towerImg,
    };
  }, [content]);

  return (
    <section className="success">
      <div className="success-container">
        <div className="success-left">
          <span className="success-tag tag">{story.tag}</span>

          <h2>{story.title}</h2>

          <p className="success-desc">{story.description}</p>

          <div className="success-location">
            <strong>{story.locationLabel}</strong>
            <p>{story.locationText}</p>
          </div>

          <button className="success-btn" type="button" onClick={() => navigate(story.buttonLink)}>
            {story.buttonText}
          </button>
        </div>

        <div className="success-right">
          <img
            src={imageSrc}
            alt={story.imageAlt}
            onError={(e) => {
              if (e.currentTarget.src !== towerImg) {
                e.currentTarget.src = towerImg;
              }
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default SuccessStory;
